import "./LegalPage.css";

const LAST_UPDATED = "June 2025";

export default function CancellationRefundPolicy() {
    return (
        <div className="legal-page">
            <div className="legal-container">

                <div className="legal-header">
                    <div className="legal-eyebrow">Legal</div>
                    <h1 className="legal-title">Cancellation & Refund Policy</h1>
                    <p className="legal-meta">Last updated: {LAST_UPDATED} · Applies to all Learnlia PRO subscriptions</p>
                </div>

                <div className="legal-notice legal-notice--info">
                    <strong>Summary:</strong> You can cancel anytime. Monthly subscribers get a 7-day full refund window.
                    Yearly subscribers get a 14-day full refund window. No partial refunds after these windows.
                </div>

                <div className="legal-body">

                    <section className="legal-section">
                        <h2>1. Subscription Plans</h2>
                        <p>
                            Learnlia offers two PRO subscription plans:
                        </p>
                        <ul>
                            <li><strong>Monthly Plan</strong> — billed every 30 days at ₹499/month (or equivalent in your currency).</li>
                            <li><strong>Yearly Plan</strong> — billed once annually at ₹2,999/year (or equivalent in your currency).</li>
                        </ul>
                        <p>
                            Subscriptions are processed through Razorpay (for Indian users) or Paddle (for international users).
                            By subscribing, you agree to automatic renewal at the end of each billing cycle unless you cancel before the renewal date.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h2>2. Free Cancellation — Anytime</h2>
                        <p>
                            You may cancel your Learnlia PRO subscription at any time. Cancellation takes effect at the end
                            of your current billing period. You will retain full PRO access until that date.
                        </p>
                        <p>
                            To cancel, go to <strong>Profile → Subscription → Cancel Plan</strong>, or email us at{" "}
                            <a href="mailto:support@learnlia.com" className="legal-link">support@learnlia.com</a> with the
                            subject line "Cancel Subscription" from your registered email address.
                        </p>
                        <p>
                            Cancellation does not delete your account, courses, progress, streaks, or earned certificates.
                            All data is retained. If you re-subscribe later, your history will be exactly as you left it.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h2>3. Refund Eligibility</h2>

                        <div className="legal-table-wrap">
                            <table className="legal-table">
                                <thead>
                                    <tr>
                                        <th>Plan</th>
                                        <th>Refund Window</th>
                                        <th>Amount</th>
                                        <th>Condition</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>Monthly</td>
                                        <td>Within 7 days of payment</td>
                                        <td>100% of amount paid</td>
                                        <td>First request only</td>
                                    </tr>
                                    <tr>
                                        <td>Yearly</td>
                                        <td>Within 14 days of payment</td>
                                        <td>100% of amount paid</td>
                                        <td>First request only</td>
                                    </tr>
                                    <tr>
                                        <td>Monthly (renewal)</td>
                                        <td>Within 48 hours of renewal charge</td>
                                        <td>100% of renewal amount</td>
                                        <td>If renewal was unintended, one-time</td>
                                    </tr>
                                    <tr>
                                        <td>Any plan</td>
                                        <td>After the window above</td>
                                        <td>₹0 (no refund)</td>
                                        <td>—</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <p>
                            Refunds are issued to the original payment method. Processing time is 5–10 business days
                            depending on your bank or card issuer. UPI refunds typically reflect within 2–5 business days.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h2>4. How to Request a Refund</h2>
                        <p>
                            Email <a href="mailto:support@learnlia.com" className="legal-link">support@learnlia.com</a> with:
                        </p>
                        <ul>
                            <li>Subject line: <code className="legal-code">Refund Request — [your registered email]</code></li>
                            <li>Your registered email address</li>
                            <li>Payment date and amount</li>
                            <li>Transaction ID or order number (from your payment confirmation email)</li>
                            <li>Brief reason (optional, but helps us improve)</li>
                        </ul>
                        <p>
                            We will acknowledge your request within 1 business day and process eligible refunds within 3 business days.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h2>5. Non-Refundable Situations</h2>
                        <p>Refunds will not be issued in the following cases:</p>
                        <ul>
                            <li>The refund window has passed (7 days for monthly, 14 days for yearly).</li>
                            <li>You have already received a refund for a previous subscription period.</li>
                            <li>Your account was suspended or terminated due to a violation of our Terms of Service.</li>
                            <li>You claim you were unaware of the subscription despite having received payment confirmation and access emails.</li>
                            <li>Partial refunds for unused days within a billing period are not offered.</li>
                        </ul>
                    </section>

                    <section className="legal-section">
                        <h2>6. Trial Periods & Promotional Pricing</h2>
                        <p>
                            If Learnlia offers a free trial period, you will not be charged until the trial ends.
                            You may cancel before the trial ends without any charge. If you are charged after a promotional
                            or discounted period, standard refund terms apply to the amount actually charged.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h2>7. Automatic Renewal Reminder</h2>
                        <p>
                            We will send you a renewal reminder email at least 3 days before your subscription renews.
                            If you do not wish to renew, cancel before the renewal date via your profile or by contacting us.
                            Refunds for charges after the 48-hour window following an unintended renewal are at our discretion
                            and limited to one occurrence per account.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h2>8. Disputes & Payment Processor Chargebacks</h2>
                        <p>
                            We strongly encourage you to contact us directly before initiating a chargeback with your bank
                            or card issuer. Most issues are resolved within 1 business day when you reach out to us first.
                            Filing a chargeback without contacting us may result in immediate account suspension pending
                            resolution. We will provide full transaction records to payment processors in the event of a dispute.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h2>9. Changes to This Policy</h2>
                        <p>
                            We may update this policy from time to time. Changes will be posted on this page with a revised
                            "Last updated" date. Continued use of Learnlia PRO after changes constitutes acceptance of the updated policy.
                            For material changes, we will notify subscribers by email.
                        </p>
                    </section>

                    <section className="legal-section">
                        <h2>10. Contact Us</h2>
                        <p>For all subscription, billing, and refund queries:</p>
                        <div className="legal-contact-block">
                            <div className="legal-contact-row">
                                <span className="legal-contact-label">Email</span>
                                <a href="mailto:support@learnlia.com" className="legal-link">support@learnlia.com</a>
                            </div>
                            <div className="legal-contact-row">
                                <span className="legal-contact-label">Response time</span>
                                <span>Within 1 business day (Mon–Sat, 9 AM – 6 PM IST)</span>
                            </div>
                            <div className="legal-contact-row">
                                <span className="legal-contact-label">Registered as</span>
                                <span>Learnlia (India) · Tamil Nadu, India</span>
                            </div>
                        </div>
                    </section>

                </div>
            </div>
        </div>
    );
}